/**
 * AJAX Faceted Filtering for Shopify
 * Uses capture phase to intercept events before other scripts
 */
(function() {
  'use strict';

  const SELECTORS = {
    form: 'form.faceted-filter-form',
    checkbox: '.faceted-filter-group-display__list-item-input[type="checkbox"]',
    submitBtn: '.faceted-filter-group-display__submit input[type="submit"]',
    clearLink: '.faceted-active-filters__remove-filter, .faceted-filter-group-display__header-clear, .faceted-active-filters__clear',
    sortBy: 'select.sort_by, select[data-sort-by], #sort-by',
    productGrid: '.product-list--collection, [data-product-list]',
    sidebar: '.sidebar-wrap',
    section: '.shopify-section--collection-template',
    pagination: '.pagination',
    hasSidebarOption: '.has-sidebar-option'
  };

  let isLoading = false;
  let initialized = false;

  function init() {
    if (initialized) return;
    
    const form = document.querySelector(SELECTORS.form);
    if (!form) return;
    
    initialized = true;

    // Use capture phase (true) to intercept before bubbling phase handlers
    
    // Capture form submit
    document.addEventListener('submit', function(e) {
      if (e.target.matches && e.target.matches(SELECTORS.form)) {
        e.preventDefault();
        e.stopImmediatePropagation();
        submitFilters();
      }
    }, true);

    // Capture checkbox changes
    document.addEventListener('change', function(e) {
      // Checkbox filter
      if (e.target.matches && e.target.matches(SELECTORS.checkbox)) {
        e.preventDefault();
        e.stopImmediatePropagation();
        submitFilters();
        return;
      }
      
      // Sort by select
      if (e.target.matches && e.target.matches(SELECTORS.sortBy)) {
        e.preventDefault();
        e.stopImmediatePropagation();
        handleSortChange(e.target.value);
        return;
      }
    }, true);

    // Capture clicks
    document.addEventListener('click', function(e) {
      // Submit button
      if (e.target.matches && e.target.matches(SELECTORS.submitBtn)) {
        e.preventDefault();
        e.stopImmediatePropagation();
        submitFilters();
        return;
      }

      // Clear links
      const clearLink = e.target.closest ? e.target.closest(SELECTORS.clearLink) : null;
      if (clearLink) {
        e.preventDefault();
        e.stopImmediatePropagation();
        const url = clearLink.getAttribute('href');
        if (url) fetchPage(url);
        return;
      }

      // Pagination
      const pageLink = e.target.closest ? e.target.closest('.pagination a') : null;
      if (pageLink) {
        e.preventDefault();
        e.stopImmediatePropagation();
        const url = pageLink.getAttribute('href');
        if (url) {
          fetchPage(url);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }
    }, true);

    // Browser back/forward
    window.addEventListener('popstate', function() {
      fetchPage(location.href, false);
    });
  }

  function handleSortChange(sortValue) {
    if (isLoading || !sortValue) return;
    
    // Get current URL and update sort_by parameter
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', sortValue);
    
    fetchPage(url.pathname + url.search);
  }

  function submitFilters() {
    if (isLoading) return;

    const form = document.querySelector(SELECTORS.form);
    if (!form) return;

    const data = new FormData(form);
    const params = new URLSearchParams();

    for (const [key, value] of data.entries()) {
      if (value) params.append(key, value);
    }
    
    // Preserve sort_by from URL if exists
    const currentUrl = new URL(window.location.href);
    const currentSort = currentUrl.searchParams.get('sort_by');
    if (currentSort) {
      params.set('sort_by', currentSort);
    }

    const url = location.pathname + (params.toString() ? '?' + params.toString() : '');
    fetchPage(url);
  }

  function fetchPage(url, pushState = true) {
    if (isLoading) return;
    isLoading = true;

    showLoading();

    // Get section ID
    const section = document.querySelector(SELECTORS.section);
    const sectionId = section ? section.id.replace('shopify-section-', '') : '';

    const fetchUrl = url + (url.includes('?') ? '&' : '?') + 'section_id=' + sectionId;

    fetch(fetchUrl)
      .then(res => res.text())
      .then(html => {
        updatePage(html);
        if (pushState) {
          history.pushState({}, '', url);
        }
        hideLoading();
        isLoading = false;
        reinitComponents();
      })
      .catch(err => {
        console.error('Filter error:', err);
        hideLoading();
        isLoading = false;
        location.href = url;
      });
  }

  function updatePage(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');

    // Update the whole content area (has-sidebar-option contains products and pagination)
    const contentArea = document.querySelector(SELECTORS.hasSidebarOption);
    const newContentArea = doc.querySelector(SELECTORS.hasSidebarOption);
    if (contentArea && newContentArea) {
      contentArea.innerHTML = newContentArea.innerHTML;
    } else {
      // Fallback: update products only
      const products = document.querySelector(SELECTORS.productGrid);
      const newProducts = doc.querySelector(SELECTORS.productGrid);
      if (products && newProducts) {
        products.outerHTML = newProducts.outerHTML;
      }
    }

    // Update sidebar
    const sidebar = document.querySelector(SELECTORS.sidebar);
    const newSidebar = doc.querySelector(SELECTORS.sidebar);
    if (sidebar && newSidebar) {
      sidebar.innerHTML = newSidebar.innerHTML;
    }

    // Update pagination if not inside content area
    const pagination = document.querySelector(SELECTORS.pagination);
    const newPagination = doc.querySelector(SELECTORS.pagination);
    if (pagination && !contentArea) {
      pagination.innerHTML = newPagination ? newPagination.innerHTML : '';
    }
    
    // Update sort select to match new state
    const sortSelect = document.querySelector(SELECTORS.sortBy);
    const newSortSelect = doc.querySelector(SELECTORS.sortBy);
    if (sortSelect && newSortSelect) {
      sortSelect.value = newSortSelect.value;
    }
  }
  
  function reinitComponents() {
    // 1. Reinitialize Flickity for product galleries
    reinitFlickity();
    
    // 2. Reinitialize lazy loading
    reinitLazyLoad();
    
    // 3. Trigger custom event for other scripts
    document.dispatchEvent(new CustomEvent('faceted:filtered', { bubbles: true }));
    
    // 4. Trigger resize/scroll for lazy loaders
    window.dispatchEvent(new Event('resize'));
    window.dispatchEvent(new Event('scroll'));
  }
  
  function reinitFlickity() {
    // Check if Flickity is available
    if (typeof Flickity === 'undefined') return;
    
    // Find all product galleries that need initialization
    const galleries = document.querySelectorAll('.js-product-gallery');
    
    galleries.forEach(function(gallery) {
      // Skip if already initialized
      if (gallery.classList.contains('flickity-enabled')) return;
      
      try {
        new Flickity(gallery, {
          cellAlign: 'left',
          contain: true,
          prevNextButtons: false,
          pageDots: false,
          draggable: false,
          wrapAround: false,
          selectedAttraction: 0.2,
          friction: 0.8
        });
      } catch (e) {
        console.log('Flickity init error:', e);
      }
    });
  }
  
  function reinitLazyLoad() {
    // Use lazySizes if available
    if (typeof lazySizes !== 'undefined') {
      lazySizes.init();
    }
    
    // Force load all lazyload images manually
    document.querySelectorAll('img.lazyload').forEach(function(img) {
      const dataSrc = img.getAttribute('data-src');
      const dataSrcset = img.getAttribute('data-srcset');
      
      if (dataSrc) {
        img.src = dataSrc;
      }
      if (dataSrcset) {
        img.srcset = dataSrcset;
      }
      
      img.classList.remove('lazyload');
      img.classList.add('lazyloaded');
    });
  }

  function showLoading() {
    document.body.classList.add('is-filtering');
    
    const grid = document.querySelector(SELECTORS.productGrid);
    if (grid && !grid.querySelector('.filter-loader')) {
      grid.style.position = 'relative';
      grid.insertAdjacentHTML('beforeend', 
        '<div class="filter-loader" style="position:absolute;top:0;left:0;right:0;bottom:0;background:rgba(255,255,255,0.7);display:flex;align-items:center;justify-content:center;z-index:10;min-height:200px;"><div style="width:30px;height:30px;border:3px solid #ddd;border-top-color:#333;border-radius:50%;animation:spin 0.8s linear infinite"></div></div>'
      );
    }
  }

  function hideLoading() {
    document.body.classList.remove('is-filtering');
    const loader = document.querySelector('.filter-loader');
    if (loader) loader.remove();
  }

  // Add animation keyframes
  if (!document.querySelector('#filter-anim-style')) {
    const style = document.createElement('style');
    style.id = 'filter-anim-style';
    style.textContent = '@keyframes spin{to{transform:rotate(360deg)}}';
    document.head.appendChild(style);
  }

  // Initialize immediately and on DOMContentLoaded
  init();
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  }
})();
